import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './EducationProgramLevelsForm.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function EducationProgramLevelsForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [formData, setFormData] = useState({
    levels: []
  });

  const [pendingFields, setPendingFields] = useState({});
  const [originalData, setOriginalData] = useState({ levels: [] });

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [imageProgress, setImageProgress] = useState({});
  const [editingLevelIndex, setEditingLevelIndex] = useState(null);
  const [modalLevelData, setModalLevelData] = useState(null);
  const [mediaMap, setMediaMap] = useState({});

  useEffect(() => {
    // Debug: Check if authenticated
    fetch(`${apiBase}/auth/me`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        console.log('[EducationProgramLevelsForm] Auth status:', data);
      })
      .catch(err => console.log('[EducationProgramLevelsForm] Auth check failed:', err.message));
    
    loadMediaMap();
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/education/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        }
      );
      
      if (!response.ok) return;
      
      const result = await response.json();
      
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path;
          
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
          }
        });
        
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[EducationProgramLevelsForm] Media fetch error:', err.message);
    }
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      console.log('[EducationProgramLevelsForm] Loading education program levels section...');
      
      // Try two approaches: first with admin auth, then fallback to public
      let levelSection = null;
      
      // Approach 1: Try admin endpoint (requires auth)
      console.log('[EducationProgramLevelsForm] Trying authenticated endpoint...');
      const adminResponse = await fetch(
        `${apiBase}/education/cms/sections`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        
        if (result.ok && Array.isArray(result.data)) {
          levelSection = result.data.find(s => s.section_type === 'education-program-levels-shell');
          console.log('[EducationProgramLevelsForm] ✓ Got data from admin endpoint');
        }
      } else if (adminResponse.status === 401) {
        console.log('[EducationProgramLevelsForm] Admin endpoint unauthorized, trying public endpoint...');
      } else {
        console.warn('[EducationProgramLevelsForm] Admin endpoint returned:', adminResponse.status);
      }
      
      // Approach 2: Fallback to public endpoint
      if (!levelSection) {
        const publicResponse = await fetch(
          `${apiBase}/education/cms/sections?published=true`,
          { 
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );
        
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          
          if (result.ok && Array.isArray(result.data)) {
            levelSection = result.data.find(s => s.section_type === 'education-program-levels-shell');
            console.log('[EducationProgramLevelsForm] ✓ Got data from public endpoint');
          }
        } else {
          throw new Error(`Public endpoint returned ${publicResponse.status}`);
        }
      }
      
      if (!levelSection) {
        throw new Error('Levels section not found');
      }

      const sectionId = levelSection.id;

      // Fetch media (public endpoint)
      try {
        const mediaResponse = await fetch(`${apiBase}/education/cms/media`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (mediaResponse.ok) {
          const mediaResult = await mediaResponse.json();
          if (mediaResult.ok && Array.isArray(mediaResult.data)) {
            console.log('[EducationProgramLevelsForm] Media loaded:', mediaResult.data.length);
          }
        }
      } catch (err) {
        console.warn('[EducationProgramLevelsForm] Media fetch error:', err.message);
      }

      setSectionId(sectionId);

      // Check for pending changes
      let pendingFieldsMap = {};
      
      try {
        const pendingResponse = await fetch(
          `${apiBase}/cms/section/${levelSection.id}/with-pending`,
          { credentials: 'include', headers: { 'Content-Type': 'application/json' } }
        );
        
        if (pendingResponse.ok) {
          const pendingResult = await pendingResponse.json();
          if (pendingResult.success && pendingResult.data && pendingResult.data.pending_changes) {
            const { pending_changes } = pendingResult.data;
            if (Array.isArray(pending_changes)) {
              pending_changes.forEach(change => {
                if (change.status === 'pending' && change.locale === 'en') {
                  if (change.field_id && change.field_id.includes('levels.')) {
                    const match = change.field_id.match(/levels\.(\d+)\./);
                    if (match) {
                      const levelIdx = parseInt(match[1]);
                      pendingFieldsMap[`level_${levelIdx}`] = true;
                    }
                  }
                }
              });
            }
          }
        }
      } catch (err) {
        console.warn('[EducationProgramLevelsForm] Pending changes check failed:', err.message);
      }

      // Extract published content
      let published = levelSection.content_published;
      if (typeof published === 'string') {
        try {
          published = JSON.parse(published);
        } catch (e) {
          published = {};
        }
      }

      const publishedEn = published.en || {};
      const levels = Array.isArray(publishedEn.levels) ? publishedEn.levels : [];

      const initialData = { levels };

      setFormData(initialData);
      setOriginalData(initialData);
      setPendingFields(pendingFieldsMap);
    } catch (err) {
      console.error('Load error:', err);
      toast.error('Error loading form: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setModalLevelData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (levelIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Max 5MB, got ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    setImageUploading(prev => ({ ...prev, [levelIndex]: true }));
    setImageProgress(prev => ({ ...prev, [levelIndex]: 0 }));

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setImageProgress(prev => ({ ...prev, [levelIndex]: percent }));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id;
              if (mediaId) {
                setModalLevelData(prev => ({...prev, image: mediaId}));
                toast.success('✓ Image uploaded');
              }
            } else {
              toast.error(response.error || 'Upload failed');
            }
          } catch (parseErr) {
            toast.error('Invalid server response');
          }
        } else {
          let errorMsg = 'Upload failed';
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.error) {
              errorMsg = response.error;
              if (xhr.status === 401) {
                errorMsg = '⚠ Not authenticated - please log in again';
              }
            }
          } catch (e) {
            if (xhr.status === 401) {
              errorMsg = '⚠ Not authenticated (HTTP 401) - session may have expired';
            }
          }
          console.error('[EducationProgramLevelsForm] Upload failed:', xhr.status, errorMsg);
          toast.error(errorMsg);
        }
        setImageUploading(prev => ({ ...prev, [levelIndex]: false }));
      });

      xhr.addEventListener('error', () => {
        toast.error('Upload failed - network error');
        setImageUploading(prev => ({ ...prev, [levelIndex]: false }));
      });

      xhr.open('POST', `${apiBase}/education/cms/media/upload`);
      xhr.withCredentials = true;
      xhr.send(formDataToUpload);
    } catch (err) {
      toast.error('Upload error: ' + err.message);
      setImageUploading(prev => ({ ...prev, [levelIndex]: false }));
    }
  };

  const handleSubmitLevel = async (levelIndex) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const level = modalLevelData;
    if (!level || !level.title) {
      toast.warning('Please fill in the title');
      return;
    }

    const levelKey = `level_${levelIndex}`;
    setSubmitting(prev => ({ ...prev, [levelKey]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `levels.${levelIndex}.update`,
            field_name: `Update Level ${levelIndex + 1}: ${level.title}`,
            old_value: originalData.levels[levelIndex] || {},
            new_value: level,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`Level ${levelIndex + 1} submitted for approval`);
        
        setPendingFields(prev => ({
          ...prev,
          [`level_${levelIndex}`]: true
        }));

        setFormData(prev => ({
          ...prev,
          levels: prev.levels.map((l, i) => i === levelIndex ? level : l)
        }));

        setOriginalData(prev => ({
          ...prev,
          levels: prev.levels.map((l, i) => i === levelIndex ? level : l)
        }));

        setEditingLevelIndex(null);
        setModalLevelData(null);
      } else {
        if (response.status === 409) {
          toast.error(`Level ${levelIndex + 1} already has a pending approval`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [levelKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="education-levels-form-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          Loading Education Program Levels...
        </div>
      </div>
    );
  }

  if (formData.levels.length === 0) {
    return (
      <div className="education-levels-form-container">
        <div className="hero-form-wrapper">
          <div className="items-empty-state">
            <div className="empty-icon">📚</div>
            <h4>No levels found</h4>
            <p>Program levels will appear here</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="education-levels-form-container">
      <ToastContainer />

      <div className="hero-form-wrapper">
        <div className="hero-form-header">
          <h2 className="hero-form-title">Education Program Levels</h2>
          <p className="hero-form-description">Manage each program level individually</p>
        </div>

        {/* LEVELS CARDS GRID */}
        <div className="form-section">
          <h3 className="form-section-title">Program Levels</h3>
          
          {formData.levels.length === 0 ? (
            <div className="items-empty-state">
              <div className="empty-icon">📚</div>
              <h4>No levels found</h4>
              <p>Program levels will appear here</p>
            </div>
          ) : (
            <div className="level-cards-grid">
              {formData.levels.map((level, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="level-card"
                  onClick={() => {
                    setEditingLevelIndex(idx);
                    setModalLevelData({...level});
                  }}
                >
                  {level.image && mediaMap[level.image] && (
                    <div className="level-card-image">
                      <img src={mediaMap[level.image]} alt={level.title} />
                    </div>
                  )}
                  {!level.image && (
                    <div className="level-card-image-placeholder">
                      📸
                    </div>
                  )}
                  
                  <div className="level-card-content">
                    <div className="level-card-header">
                      <h4 className="level-card-title">{level.title}</h4>
                      {pendingFields[`level_${idx}`] && (
                        <span className="level-card-badge">⏳</span>
                      )}
                    </div>
                    {level.subtitle && (
                      <p className="level-card-subtitle">{level.subtitle}</p>
                    )}
                    {level.description && (
                      <p className="level-card-desc">{level.description.substring(0, 60)}...</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* EDIT LEVEL MODAL */}
      {editingLevelIndex !== null && modalLevelData && (
        <div className="modal-overlay" onClick={() => {
          setEditingLevelIndex(null);
          setModalLevelData(null);
        }}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Level {editingLevelIndex + 1}</h3>
              <button className="modal-close" onClick={() => {
                setEditingLevelIndex(null);
                setModalLevelData(null);
              }}>×</button>
            </div>

            <div className="modal-body">
              {/* IMAGE PREVIEW */}
              <div className="modal-field">
                <label>Level Image</label>
                {modalLevelData.image && mediaMap[modalLevelData.image] && (
                  <div className="modal-image-preview">
                    <img src={mediaMap[modalLevelData.image]} alt="Level preview" />
                  </div>
                )}
                <div className="image-upload-area">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => handleImageUpload(editingLevelIndex, e)}
                    disabled={imageUploading[editingLevelIndex]}
                    className="image-input"
                    id={`modal-level-image-input-${editingLevelIndex}`}
                  />
                  <label htmlFor={`modal-level-image-input-${editingLevelIndex}`} className={`upload-label ${imageUploading[editingLevelIndex] ? 'uploading' : ''}`}>
                    {imageUploading[editingLevelIndex] ? `Uploading ${imageProgress[editingLevelIndex]}%...` : 'Upload Image'}
                  </label>
                </div>
                <span className="hint">Max 5MB • Recommended: 600×400px • JPG, PNG, WebP</span>
              </div>

              {/* TITLE FIELD */}
              <div className="modal-field">
                <label>Title *</label>
                <input
                  type="text"
                  maxLength="100"
                  value={modalLevelData.title}
                  onChange={(e) => handleFieldChange('title', e.target.value)}
                  placeholder="e.g., Nursery (Ages 3-4)"
                  className="modal-input"
                />
              </div>

              {/* SUBTITLE FIELD */}
              <div className="modal-field">
                <label>Subtitle / Age Range</label>
                <input
                  type="text"
                  maxLength="50"
                  value={modalLevelData.subtitle}
                  onChange={(e) => handleFieldChange('subtitle', e.target.value)}
                  placeholder="e.g., Ages 3-4"
                  className="modal-input"
                />
              </div>

              {/* DESCRIPTION FIELD */}
              <div className="modal-field">
                <label>Description</label>
                <textarea
                  maxLength="500"
                  value={modalLevelData.description}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  placeholder="Program description..."
                  className="modal-textarea"
                  rows="4"
                />
              </div>

              {/* LINK FIELD - OPTIONAL */}
              <div className="modal-field">
                <label>Link to Details Page (Optional)</label>
                <input
                  type="url"
                  maxLength="500"
                  value={modalLevelData.link || ''}
                  onChange={(e) => handleFieldChange('link', e.target.value)}
                  placeholder="e.g., https://example.com/program/nursery"
                  className="modal-input"
                />
                <span className="hint">Optional: Link to more details about this program level</span>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => {
                setEditingLevelIndex(null);
                setModalLevelData(null);
              }}>Cancel</button>
              <button
                className="btn-submit"
                disabled={submitting[`level_${editingLevelIndex}`] || !modalLevelData.title}
                onClick={() => handleSubmitLevel(editingLevelIndex)}
              >
                {submitting[`level_${editingLevelIndex}`] ? 'Submitting...' : 'Save & Submit for Approval'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
